// material-ui
import { Theme, useTheme } from '@mui/material/styles';
import MuiChip, { ChipProps } from '@mui/material/Chip';
import { SxProps } from '@mui/system';
import { ReactElement, memo, useMemo } from 'react';
import { Icon } from '@tabler/icons-react';
import _ from 'lodash';
import { Tooltip, TooltipProps } from '@mui/material';

// ==============================|| CHIP ||============================== //

export interface chipProps extends ChipProps {
  chipcolor?: ChipColor;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  label?: string;
  avatar?: ReactElement | undefined;
  onDelete?: () => void;
  keepColorWhenDisabled?: boolean;
  type?: 'chip' | 'button' | 'tag';
  ChipIcon?: Icon;
  outlineBgColor?: string;
  tooltip?: string | ReactElement;
  tooltipSlotProps?: TooltipProps['slotProps'];
  tooltipPlacement?: TooltipProps['placement'];
  iconColorOverride?: string;
}

export enum ChipColor {
  secondary = 'secondary',
  success = 'success',
  warning = 'warning',
  error = 'error',
  primary = 'primary',
  default = 'default',
  orange = 'orange',
  grey = 'grey',
}

const defaultSXProp = {};

const Chip = memo(
  ({
    chipcolor,
    disabled,
    keepColorWhenDisabled,
    sx = defaultSXProp,
    variant,
    type = 'chip',
    onClick,
    title,
    label,
    style,
    ChipIcon,
    icon,
    outlineBgColor = 'transparent',
    tooltip,
    tooltipSlotProps,
    tooltipPlacement = 'top',
    iconColorOverride,
    ...others
  }: chipProps) => {
    const theme = useTheme();
    const SX = useMemo(() => {
      let defaultSX = {
        color: theme.palette.primary.main,
        borderRadius:
          type === 'tag' ? '0 6px 6px 0' : type === 'chip' ? undefined : '6px',
        bgcolor:
          theme.palette.mode === 'dark'
            ? theme.palette.primary.main
            : theme.palette.primary.light,
        ...(type === 'tag' && {
          '& .MuiChip-label': {
            paddingLeft: '8px',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -10,
            top: 0,
            bottom: 0,
            borderTop: '15px solid transparent',
            borderBottom: '15px solid transparent',
            borderTopLeftRadius: '6px',
            borderRight: `10px solid ${
              theme.palette.mode === 'dark'
                ? theme.palette.primary.main
                : theme.palette.primary.light
            }`,
          },
        }),
      };

      let outlineSX = {
        color: theme.palette.primary.main,
        bgcolor: outlineBgColor,
        border: '1px solid',
        borderRadius:
          type === 'tag' ? '0 6px 6px 0' : type === 'chip' ? undefined : '6px',
        borderColor: theme.palette.primary.main,
        ':hover': {
          color:
            theme.palette.mode === 'dark'
              ? theme.palette.primary.light
              : theme.palette.primary.light,
          bgcolor:
            theme.palette.mode === 'dark'
              ? theme.palette.primary.main
              : theme.palette.primary.dark,
        },
        ...(type === 'tag' && {
          '& .MuiChip-label': {
            paddingLeft: '8px',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: -10,
            top: 0,
            bottom: 0,
            borderTop: '15px solid transparent',
            borderBottom: '15px solid transparent',
            borderTopLeftRadius: '6px',
            borderRight: `10px solid ${theme.palette.primary.main}`,
          },
        }),
      };
      switch (chipcolor) {
        case 'secondary':
          variant === 'outlined'
            ? (outlineSX = {
                color: theme.palette.secondary.main,
                bgcolor: outlineBgColor,
                border: '1px solid',
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                borderColor: theme.palette.secondary.main,
                ':hover': {
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.secondary.light
                      : theme.palette.secondary.main,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.secondary.dark
                      : theme.palette.secondary.light,
                },
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${theme.palette.secondary.main}`,
                  },
                }),
              })
            : (defaultSX = {
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.secondary.light
                    : theme.palette.secondary.main,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.secondary.dark
                    : theme.palette.secondary.light,
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${
                      theme.palette.mode === 'dark'
                        ? theme.palette.secondary.dark
                        : theme.palette.secondary.light
                    }`,
                  },
                }),
              });
          break;
        case 'success':
          variant === 'outlined'
            ? (outlineSX = {
                color: theme.palette.success.dark,
                bgcolor: outlineBgColor,
                border: '1px solid',
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                borderColor: theme.palette.success.dark,
                ':hover': {
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.success.light
                      : theme.palette.success.dark,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.success.dark
                      : theme.palette.success.light,
                },
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${theme.palette.success.dark}`,
                  },
                }),
              })
            : (defaultSX = {
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.success.light
                    : theme.palette.success.dark,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.success.dark
                    : theme.palette.success.light,
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${
                      theme.palette.mode === 'dark'
                        ? theme.palette.success.dark
                        : theme.palette.success.light
                    }`,
                  },
                }),
              });
          break;
        case 'error':
          variant === 'outlined'
            ? (outlineSX = {
                color: theme.palette.error.main,
                bgcolor: outlineBgColor,
                border: '1px solid',
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                borderColor: theme.palette.error.main,
                ':hover': {
                  color:
                    theme.palette.mode === 'dark'
                      ? theme.palette.error.light
                      : theme.palette.error.dark,
                  bgcolor:
                    theme.palette.mode === 'dark'
                      ? theme.palette.error.dark
                      : theme.palette.error.light,
                },
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${theme.palette.error.main}`,
                  },
                }),
              })
            : (defaultSX = {
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                color:
                  theme.palette.mode === 'dark'
                    ? theme.palette.error.light
                    : theme.palette.error.dark,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.error.dark
                    : theme.palette.error.light,
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${
                      theme.palette.mode === 'dark'
                        ? theme.palette.error.dark
                        : theme.palette.error.light
                    }`,
                  },
                }),
              });
          break;
        case 'orange':
          variant === 'outlined'
            ? (outlineSX = {
                color: theme.palette.orange?.dark!,
                bgcolor: outlineBgColor,
                border: '1px solid',
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                borderColor: theme.palette.orange?.main!,
                ':hover': {
                  color: theme.palette.orange?.dark!,
                  bgcolor: theme.palette.orange?.light!,
                },
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${theme.palette.orange?.main}`,
                  },
                }),
              })
            : (defaultSX = {
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                color: theme.palette.orange?.dark!,
                bgcolor: theme.palette.orange?.light!,
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${theme.palette.orange?.light}`,
                  },
                }),
              });
          break;
        case 'grey':
          variant === 'outlined'
            ? (outlineSX = {
                color: theme.palette.grey?.['500'],
                bgcolor: outlineBgColor,
                border: '1px solid',
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                borderColor: theme.palette.grey?.['500'],
                ':hover': {
                  color: theme.palette.grey?.['500'],
                  bgcolor: theme.palette.grey?.['50'],
                },
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${theme.palette.grey?.['500']}`,
                  },
                }),
              })
            : (defaultSX = {
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                color: theme.palette.grey?.['500'],
                bgcolor: theme.palette.grey?.['50'],
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${theme.palette.grey?.['50']}`,
                  },
                }),
              });
          break;
        case 'warning':
          variant === 'outlined'
            ? (outlineSX = {
                color: theme.palette.warning.dark,
                bgcolor: outlineBgColor,
                border: '1px solid',
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                borderColor: theme.palette.warning.dark,
                ':hover': {
                  color: theme.palette.warning.dark,
                  bgcolor: theme.palette.warning.light,
                },
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${theme.palette.warning.dark}`,
                  },
                }),
              })
            : (defaultSX = {
                borderRadius:
                  type === 'tag'
                    ? '0 6px 6px 0'
                    : type === 'chip'
                    ? undefined
                    : '6px',
                color: theme.palette.warning.dark,
                bgcolor: theme.palette.warning.light,
                ...(type === 'tag' && {
                  '& .MuiChip-label': {
                    paddingLeft: '8px',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: -10,
                    top: 0,
                    bottom: 0,
                    borderTop: '15px solid transparent',
                    borderBottom: '15px solid transparent',
                    borderTopLeftRadius: '6px',
                    borderRight: `10px solid ${theme.palette.warning.light}`,
                  },
                }),
              });
          break;
        default:
      }

      if (disabled && !keepColorWhenDisabled) {
        variant === 'outlined'
          ? (outlineSX = {
              color: theme.palette.grey[500],
              bgcolor: outlineBgColor,
              border: '1px solid',
              borderRadius:
                type === 'tag'
                  ? '0 6px 6px 0'
                  : type === 'chip'
                  ? undefined
                  : '6px',
              borderColor: theme.palette.grey[500],
              ':hover': {
                color: theme.palette.grey[500],
                bgcolor: 'transparent',
              },
              ...(type === 'tag' && {
                '& .MuiChip-label': {
                  paddingLeft: '8px',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -10,
                  top: 0,
                  bottom: 0,
                  borderTop: '15px solid transparent',
                  borderBottom: '15px solid transparent',
                  borderTopLeftRadius: '6px',
                  borderRight: `10px solid ${theme.palette.grey[500]}`,
                },
              }),
            })
          : (defaultSX = {
              borderRadius:
                type === 'tag'
                  ? '0 6px 6px 0'
                  : type === 'chip'
                  ? undefined
                  : '6px',
              color: theme.palette.grey[500],
              bgcolor: theme.palette.grey[50],
              ...(type === 'tag' && {
                '& .MuiChip-label': {
                  paddingLeft: '8px',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  left: -10,
                  top: 0,
                  bottom: 0,
                  borderTop: '15px solid transparent',
                  borderBottom: '15px solid transparent',
                  borderTopLeftRadius: '6px',
                  borderRight: `10px solid ${theme.palette.grey[50]}`,
                },
              }),
            });
      }

      let SXObject: SxProps<Theme> = defaultSX;
      if (variant === 'outlined') {
        SXObject = outlineSX;
      }
      return SXObject;
    }, [
      theme.palette,
      variant,
      type,
      chipcolor,
      disabled,
      keepColorWhenDisabled,
      outlineBgColor,
    ]);

    const chipElement = (
      <MuiChip
        {...others}
        onClick={onClick}
        title={title}
        label={label}
        style={
          type === 'tag'
            ? { ...style, marginLeft: 10, position: 'relative' }
            : style
        }
        sx={{ ...SX, ...sx }}
        disabled={disabled}
        icon={
          ChipIcon ? (
            <ChipIcon color={iconColorOverride ?? _.get(SX, 'color')} />
          ) : (
            icon
          )
        }
      />
    );

    return tooltip ? (
      <Tooltip
        title={tooltip}
        placement={tooltipPlacement}
        slotProps={tooltipSlotProps}
      >
        {chipElement}
      </Tooltip>
    ) : (
      chipElement
    );
  }
);

export default memo(Chip);
